import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Wallet } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { formatCents, fmtDate } from "./billingMeta";

export const BillingPaymentsTab = ({ API, canManage }) => {
  const [payments, setPayments] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/org/billing/payments`, { withCredentials: true })
      .then(({ data }) => setPayments(data.payments))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="billing-payments-error">{error}</p>;

  return (
    <div data-testid="billing-payments-tab">
      <Panel title="Payments" subtitle="Every payment recorded against a customer invoice.">
        {!payments ? (
          <p className="text-sm text-slate-400" data-testid="billing-payments-loading">Loading…</p>
        ) : payments.length === 0 ? (
          <EmptyState icon={Wallet} title="No payments recorded yet." message={canManage ? "Record a payment from an invoice once it's been sent." : "Payments recorded for your Team will show up here."} testId="billing-payments-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="billing-payments-table">
              <thead><tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-400"><th className="pb-2">Date</th><th className="pb-2">Invoice #</th><th className="pb-2">Amount</th><th className="pb-2">Method</th><th className="pb-2">Status</th><th className="pb-2">Recorded By</th></tr></thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.payment_id} className="border-t border-slate-100 dark:border-white/5" data-testid={`billing-payment-row-${p.payment_id}`}>
                    <td className="py-2.5">{fmtDate(p.received_at)}</td>
                    <td className="py-2.5 font-bold text-[#0052FF]">{p.invoice_display_id}</td>
                    <td className="py-2.5 font-semibold">{formatCents(p.amount_cents)}</td>
                    <td className="py-2.5 text-slate-500 dark:text-slate-400">{p.method}</td>
                    <td className="py-2.5"><span className="rounded-full border border-emerald-200 px-2 py-0.5 text-xs font-bold text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400">{p.status}</span></td>
                    <td className="py-2.5 text-slate-500 dark:text-slate-400">{p.recorded_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
};
